import { Link } from "react-router-dom";
import { Flower, ArrowLeft, ShieldCheck, WheelchairMotion, Ear, EyeSlash, Keyboard, HandHeart, EnvelopeSimple, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

const CONFORMANCE_ITEMS = [
  {
    icon: EyeSlash,
    h: "Screen readers",
    body: "Every interactive element has a descriptive label. Streaming AI responses announce updates through ARIA live regions so nothing is missed.",
  },
  {
    icon: Keyboard,
    h: "Keyboard navigation",
    body: "Every flow — from onboarding to sending a DM to subscribing to Premium — can be completed with a keyboard alone. A visible coral focus ring shows you where you are.",
  },
  {
    icon: WheelchairMotion,
    h: "Motion & touch",
    body: "We respect the operating-system-level 'reduce motion' setting. On touch devices, every tap target meets the 44×44 pixel minimum.",
  },
  {
    icon: Ear,
    h: "Voice & audio",
    body: "The AI Health Navigator can read any response aloud in a warm, natural voice. Community summaries, hub content, and journal entries all support text-to-speech.",
  },
  {
    icon: HandHeart,
    h: "Privacy-safe accessibility",
    body: "Screen readers never announce another user's identifying information. Anonymous names stay anonymous, in every assistive-technology context, by design.",
  },
];

const KNOWN_GAPS = [
  "Some third-party embeds (Stripe hosted checkout, Emergent Google sign-in) render outside our accessibility scope. We rely on those vendors' own conformance and continue to evaluate alternatives.",
  "Custom emoji reactions (heart, hug, hope, same) currently expose accessible names but do not yet include per-language variants. This ships with our internationalization work.",
  "Alt text for community-uploaded imagery is not yet supported because we do not currently permit user-uploaded images. If we add uploads, alt text will be required by design.",
  "An independent third-party accessibility audit is scheduled for Q2 2026. This statement will be updated with the auditor's findings and any remediation work at that time.",
];

const ROADMAP = [
  { q: "Q1 2026", item: "Complete first WCAG 2.1 AA conformance pass, publish this statement, publish VPAT 2.5 INT on request." },
  { q: "Q2 2026", item: "Third-party accessibility audit by an independent testing service; publish results and remediations." },
  { q: "Q3 2026", item: "Plain-language mode toggle across the platform for lower reading-level accessibility." },
  { q: "Q4 2026", item: "Push toward WCAG 2.1 Level AAA where feasible, including dyslexia-friendly typography option and high-contrast theme." },
];

export default function Accessibility() {
  usePageMeta({
    title: "Accessibility statement",
    description: "Wingwomen is committed to WCAG 2.1 Level AA conformance and to making reproductive-health peer support accessible to everyone.",
  });

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="a11y-brand" className="flex items-center gap-2">
            <Flower size={22} weight="duotone" className="text-coral-700" aria-hidden="true" />
            <span className="font-display text-xl">Wingwomen</span>
          </Link>
          <Link to="/" className="text-sm hover:text-coral-700 flex items-center gap-1.5" data-testid="a11y-back-home">
            <ArrowLeft size={14} aria-hidden="true" /> Home
          </Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-[900px] mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
          <span className="rule" />Accessibility
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-tight" data-testid="a11y-title">
          Everyone belongs<br /><span className="italic text-coral-700">in the circle.</span>
        </h1>
        <p className="mt-6 text-lg text-ink/75 max-w-2xl leading-relaxed">
          Wingwomen is committed to being usable by everyone, including people with visual,
          hearing, motor, cognitive, and language-based disabilities. Accessibility is a
          product requirement here, not a compliance chore.
        </p>

        {/* Commitment block */}
        <section className="mt-12 bg-white border border-border rounded-2xl p-8 shadow-card" aria-labelledby="commitment-h">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-coral-50 border border-coral-100 grid place-items-center shrink-0">
              <ShieldCheck size={20} weight="duotone" className="text-coral-700" aria-hidden="true" />
            </div>
            <div>
              <h2 id="commitment-h" className="font-display text-2xl leading-tight">Our conformance target</h2>
              <p className="mt-3 text-sm text-ink/80 leading-relaxed">
                Wingwomen conforms to the{" "}
                <a
                  href="https://www.w3.org/TR/WCAG21/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-coral-700 underline underline-offset-4"
                >
                  Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA
                </a>
                . We audit our platform against this standard on a rolling basis and treat any
                confirmed non-conformance as a bug to be fixed, not a nice-to-have.
              </p>
              <p className="mt-3 text-sm text-ink/80 leading-relaxed">
                This statement covers <strong>mywingwomen.com</strong> and all authenticated
                pages under <strong>/app</strong>. It was last reviewed on the date shown at
                the bottom of this page.
              </p>
            </div>
          </div>
        </section>

        {/* What conformance means for you */}
        <section className="mt-16" aria-labelledby="what-h">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
            <span className="rule" />What this means for you
          </div>
          <h2 id="what-h" className="font-display text-3xl sm:text-4xl leading-tight">
            Built for real bodies and real days.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {CONFORMANCE_ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.h} className="bg-white border border-border rounded-2xl p-6 shadow-soft" data-testid={`a11y-item-${it.h.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}>
                  <div className="flex items-center gap-3">
                    <Icon size={22} weight="duotone" className="text-coral-700" aria-hidden="true" />
                    <div className="font-display text-lg">{it.h}</div>
                  </div>
                  <p className="mt-3 text-sm text-ink/75 leading-relaxed">{it.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Regulatory alignment */}
        <section className="mt-16" aria-labelledby="reg-h">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
            <span className="rule" />Regulatory alignment
          </div>
          <h2 id="reg-h" className="font-display text-3xl sm:text-4xl leading-tight">
            Where we sit legally.
          </h2>
          <div className="mt-6 bg-white border border-border rounded-2xl p-8 shadow-soft space-y-4 text-sm text-ink/80 leading-relaxed">
            <p>
              We design and test Wingwomen to be accessible under the standards typically
              applied to digital health services in the United States:
            </p>
            <ul className="space-y-3 list-none pl-0">
              <li className="flex items-start gap-3">
                <CheckCircle size={16} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong>Americans with Disabilities Act (ADA) Title III</strong>, as
                interpreted by federal courts to require WCAG 2.1 AA for consumer-facing
                web services.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={16} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong>HHS Section 1557 of the Affordable Care Act</strong>, whose 2024
                final rule references WCAG 2.1 AA as the accessibility standard for covered
                health programs. Wingwomen, Inc. is not currently a covered entity under
                Section 1557, but we build to it proactively.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={16} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong>Section 508 of the Rehabilitation Act</strong>, applicable to
                federal agencies and their contractors. We publish a Voluntary Product
                Accessibility Template (VPAT 2.5 INT) on request.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={16} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong>State civil rights statutes</strong> including California's Unruh
                Civil Rights Act and equivalent laws in New York, Colorado, and other
                jurisdictions.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={16} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong>Industry frameworks</strong> including the FDA's 2016 guidance on
                Applying Human Factors and Usability Engineering to Medical Devices,
                referenced for our AI Health Navigator (which is positioned as peer support
                and does not provide diagnosis or treatment).</span>
              </li>
            </ul>
            <p className="text-xs text-muted-foreground pt-2 border-t border-border">
              This is not legal advice. If you represent a health system, insurer, or agency
              with a specific accessibility conformance requirement, email us and we will
              share our current VPAT and audit results.
            </p>
          </div>
        </section>

        {/* Known gaps */}
        <section className="mt-16" aria-labelledby="gaps-h">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
            <span className="rule" />Known gaps
          </div>
          <h2 id="gaps-h" className="font-display text-3xl sm:text-4xl leading-tight">
            Where we are still working.
          </h2>
          <p className="mt-4 text-sm text-ink/75 max-w-2xl leading-relaxed">
            Transparency matters more than a perfect PDF. Here is where we currently fall
            short of full AA conformance and what we are doing about it.
          </p>
          <ul className="mt-6 space-y-4">
            {KNOWN_GAPS.map((g, i) => (
              <li key={i} className="bg-sage-50 border border-sage-100 rounded-xl p-5 text-sm text-ink/80 leading-relaxed">
                {g}
              </li>
            ))}
          </ul>
        </section>

        {/* Roadmap */}
        <section className="mt-16" aria-labelledby="roadmap-h">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
            <span className="rule" />Roadmap
          </div>
          <h2 id="roadmap-h" className="font-display text-3xl sm:text-4xl leading-tight">
            What is next.
          </h2>
          <div className="mt-6 bg-white border border-border rounded-2xl divide-y divide-border shadow-soft">
            {ROADMAP.map((r) => (
              <div key={r.q} className="p-5 flex items-start gap-4">
                <div className="text-[10px] tracking-[0.2em] uppercase text-coral-700 font-semibold shrink-0 w-20 pt-1">
                  {r.q}
                </div>
                <div className="text-sm text-ink/80 leading-relaxed">{r.item}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Report an issue */}
        <section className="mt-16 bg-ink text-cream rounded-2xl p-10 shadow-hero" aria-labelledby="report-h">
          <div className="text-xs tracking-[0.25em] uppercase text-coral-700 mb-4">
            <span className="rule bg-coral opacity-100" />Report an issue
          </div>
          <h2 id="report-h" className="font-display text-3xl sm:text-4xl leading-tight">
            Find a barrier?<br /><span className="italic text-coral-700">Tell us.</span>
          </h2>
          <p className="mt-5 text-cream/80 max-w-xl leading-relaxed">
            If any part of Wingwomen is difficult or impossible for you to use, please
            reach out. Include the page, the assistive technology you are using, and what
            happened. We aim to respond within one business day and to resolve confirmed
            issues on our next release.
          </p>
          <a
            href="mailto:hi@mywingwomen.com?subject=Accessibility%20feedback"
            data-testid="a11y-contact"
            className="mt-6 inline-flex items-center gap-2 bg-coral text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-coral-600 transition-colors"
          >
            <EnvelopeSimple size={16} weight="duotone" aria-hidden="true" />
            Email hi@mywingwomen.com <ArrowRight size={14} weight="bold" aria-hidden="true" />
          </a>
          <div className="mt-4 text-xs text-cream/70">
            Working with a compliance team?{" "}
            <Link to="/accessibility/vpat" data-testid="a11y-vpat-link" className="text-coral-300 underline underline-offset-4 hover:text-coral">
              View our full VPAT 2.5 INT →
            </Link>
          </div>
        </section>

        <p className="mt-16 text-xs text-muted-foreground">
          Last reviewed: February 2026. Wingwomen, Inc. reviews this statement quarterly.
        </p>
      </main>

      <SiteFooter compact />
    </div>
  );
}
